import React from "react";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error("ErrorBoundary caught an error:", error, info);
    
    // Check if it's a chunk load error (common during redeploys)
    const isChunkLoadError = error?.name === 'ChunkLoadError' || 
                            error?.message?.includes('Loading chunk') ||
                            error?.message?.includes('loading failed');
                            
    if (isChunkLoadError) {
      // Small delay to prevent infinite refresh loops
      const lastRefresh = localStorage.getItem('last-chunk-refresh');
      const now = Date.now();
      
      if (!lastRefresh || now - parseInt(lastRefresh) > 10000) {
        localStorage.setItem('last-chunk-refresh', now.toString());
        window.location.reload();
      }
    }
  }

  handleRefresh = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = "/";
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-[100dvh] bg-[#131314] flex items-center justify-center p-4">
          <div className="max-w-xl w-full bg-white/5 border border-white/10 rounded-3xl p-8 text-center backdrop-blur-xl shadow-2xl">
            <div className="w-20 h-20 bg-red-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="text-red-500" size={40} />
            </div>
            
            <h1 className="text-2xl font-black font-inter text-white uppercase tracking-tight mb-2">
              Something went wrong
            </h1>
            
            <p className="text-white/60 font-inter leading-relaxed mb-6">
              The application encountered an unexpected error. 
              Our team has been notified (if logging was enabled).
            </p>

            <div className="bg-black/20 rounded-2xl p-4 mb-8 text-left border border-white/5 overflow-hidden">
              <p className="text-[10px] font-black uppercase tracking-widest text-[#F4B400] mb-2">
                Technical Details
              </p>
              <pre className="text-[11px] font-mono text-white/40 whitespace-pre-wrap break-all leading-snug">
                {String(this.state.error?.message || this.state.error || "Unknown error")}
              </pre>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <button
                onClick={this.handleRefresh}
                className="flex items-center justify-center gap-2 py-4 rounded-2xl font-black text-xs uppercase tracking-widest bg-white/10 text-white border border-white/10 hover:bg-white/20 transition-all"
              >
                <RefreshCw size={16} /> Reload Page
              </button>

              <button
                onClick={this.handleGoHome}
                className="flex items-center justify-center gap-2 py-4 rounded-2xl font-black text-xs uppercase tracking-widest bg-[#F4B400] text-black hover:bg-[#F4B400]/90 transition-all"
              >
                <Home size={16} /> Go Home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
